import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';

interface MenuItem {
  title: string;
  url: string;
  icon?: LucideIcon;
}

interface MobileNavMenuProps {
  title: string;
  navLabel?: string;
  icon: LucideIcon;
  items: MenuItem[];
  isActive?: boolean;
}

export function MobileNavMenu({ title, navLabel, icon: Icon, items, isActive }: MobileNavMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: '50%', bottom: '0px' });
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  // Check if any menu item is active
  const hasActiveItem = items.some(item => location.pathname === item.url);

  // Calculate menu position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const updatePosition = () => {
        if (!buttonRef.current) return;
        
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Calculate menu width (matches CSS: max(200px, min(90vw, clamp(280px, 50vw, 400px))))
        // Step 1: clamp(280px, 50vw, 400px)
        const clamped = Math.max(280, Math.min(viewportWidth * 0.5, 400));
        // Step 2: min(90vw, clamped)
        const minWith90vw = Math.min(viewportWidth * 0.9, clamped);
        // Step 3: max(200px, minWith90vw)
        const menuWidth = Math.max(200, minWith90vw);
        
        // Calculate center position based on button center
        const buttonCenterX = buttonRect.left + buttonRect.width / 2;
        let menuLeft = buttonCenterX - menuWidth / 2;
        
        // Ensure menu stays within viewport bounds with padding
        const padding = 8;
        menuLeft = Math.max(
          padding,
          Math.min(menuLeft, viewportWidth - menuWidth - padding)
        );
        
        // Position above the button with margin
        const margin = 8;
        const bottomPosition = viewportHeight - buttonRect.top + margin;
        
        setMenuPosition({
          left: `${menuLeft}px`,
          bottom: `${bottomPosition}px`,
        });
      };
      
      updatePosition();
      
      // Update position on resize or orientation change
      window.addEventListener('resize', updatePosition);
      window.addEventListener('orientationchange', updatePosition);
      
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('orientationchange', updatePosition);
      };
    }
  }, [isOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  const routeActiveClass =
    'text-forest-green bg-forest-green/20 ring-1 ring-inset ring-forest-green/35 shadow-sm font-semibold';
  const menuOpenOnlyClass = 'text-forest-green/90 bg-forest-green/5';

  return (
    <div ref={menuRef} className="relative flex flex-col min-w-0">
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`w-full flex flex-col items-center justify-center gap-1 py-2 rounded-lg transition-all touch-manipulation min-h-[44px] min-w-0 ${
          hasActiveItem
            ? routeActiveClass
            : isOpen
              ? menuOpenOnlyClass
              : 'text-text-muted hover:text-forest-green/80'
        }`}
        aria-label={title}
        aria-expanded={isOpen}
        style={{ touchAction: 'manipulation' }}
      >
        <span className="flex items-center justify-center w-6 h-6 flex-shrink-0 relative">
          <Icon className="w-5 h-5" aria-hidden />
          {isOpen && (
            <ChevronUp className="absolute -top-0.5 -right-0.5 w-3 h-3 text-forest-green" />
          )}
        </span>
        <span className="text-[9px] sm:text-[10px] font-medium truncate w-full text-center whitespace-nowrap">
          {navLabel ?? title}
        </span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 z-40"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Menu Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="fixed bg-primary rounded-lg shadow-xl border border-forest-green/20 z-50 overflow-hidden overflow-y-auto"
              style={{
                left: menuPosition.left,
                bottom: menuPosition.bottom,
                width: 'max(200px, min(90vw, clamp(280px, 50vw, 400px)))',
                maxHeight: 'min(70vh, calc(100vh - 200px))',
              }}
            >
              <div className="py-2">
                {items.map((item) => {
                  const ItemIcon = item.icon;
                  const isItemActive = location.pathname === item.url;
                  
                  return (
                    <Link
                      key={item.url}
                      to={item.url}
                      onClick={() => setIsOpen(false)}
                      className={`flex items-center gap-3 px-5 py-3.5 transition-colors min-h-[48px] touch-manipulation whitespace-nowrap ${
                        isItemActive
                          ? 'bg-forest-green/10 text-forest-green font-semibold'
                          : 'text-text-muted active:bg-forest-green/5'
                      }`}
                      style={{ touchAction: 'manipulation' }}
                    >
                      {ItemIcon && <ItemIcon className="w-5 h-5 flex-shrink-0" style={{ minWidth: '20px', minHeight: '20px' }} />}
                      <span className="text-base font-medium flex-1">{item.title}</span>
                    </Link>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
